import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Breadcrum from '../../components/common/Breadcrum';
import ServerTable from '../../components/table/ServerTable';
import Select from '../../components/form/Select';
import Models from '../../components/common/Models';
import moment from 'moment';
import axiosInstance from '../../services/axios';
import { toast } from 'react-toastify';

export default function EmployeeTodoList() {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [priorityFilter, setPriorityFilter] = useState('ALL');
  const [tableRow, setTableRow] = useState([]);
  const [loading, setLoading] = useState(false);
  const [todoList, setTodoList] = useState([]);
  const [totalTodos, setTotalTodos] = useState(0);
  const [todoStats, setTodoStats] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedTaskForStatus, setSelectedTaskForStatus] = useState(null);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/employee/dashboard' },
    { label: 'My Tasks', path: '/employee/todo-list' }
  ];

  const statusOptions = [
    { value: "ALL", label: "All Status" },
    { value: "PENDING", label: "Pending" },
    { value: "IN_PROGRESS", label: "In Progress" },
    { value: "COMPLETED", label: "Completed" }
  ];

  const priorityOptions = [
    { value: "ALL", label: "All Priority" },
    { value: "LOW", label: "Low" },
    { value: "MEDIUM", label: "Medium" },
    { value: "HIGH", label: "High" },
    { value: "URGENT", label: "Urgent" }
  ];

  const tableColumns = [
    { id: 'serialNo', label: 'S.No', type: 'text' },
    { id: 'title', label: 'Task Title', type: 'text' },
    { id: 'description', label: 'Description', type: 'text' },
    { id: 'priority', label: 'Priority', type: 'badge' },
    { id: 'status', label: 'Status', type: 'badge' },
    { id: 'dueDate', label: 'Due Date', type: 'date' },
    { id: 'assignedBy', label: 'Assigned By', type: 'text' },
    { id: 'createdAt', label: 'Created', type: 'date' },
    { id: 'actions', label: 'Actions', type: 'custom' }
  ];

  const getMyTodos = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        priority: priorityFilter === 'ALL' ? undefined : priorityFilter
      };

      const response = await axiosInstance.get('/todos/my-todos', { params });
      setTodoList(response.data.data.todos || []);
      setTotalTodos(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch my tasks');
      setTodoList([]);
    } finally {
      setLoading(false);
    }
  };

  const getTodoStats = async () => {
    try {
      const response = await axiosInstance.get('/todos/stats');
      setTodoStats(response.data.data);
    } catch (error) {
      console.error('Failed to fetch todo stats');
    }
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      await axiosInstance.put(`/todos/${taskId}`, { status });
      toast.success(`Task marked as ${status.toLowerCase().replace('_', ' ')}`);
      getMyTodos();
    } catch (error) {
      toast.error('Failed to update task status');
    }
  };

  const addComment = async (taskId, commentText) => {
    try {
      await axiosInstance.post(`/todos/${taskId}/comments`, { comment: commentText });
      toast.success('Comment added successfully');
      setComment('');
      setIsCommentModalOpen(false);
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const handleTableAction = async (action, row) => {
    if (action === 'view') {
      setSelectedTask(row);
      setIsViewModalOpen(true);
    } else if (action === 'changeStatus') {
      setSelectedTaskForStatus(row);
      setIsStatusModalOpen(true);
    } else if (action === 'markProgress') {
      await updateTaskStatus(row.uuid, 'IN_PROGRESS');
    } else if (action === 'markCompleted') {
      await updateTaskStatus(row.uuid, 'COMPLETED');
    } else if (action === 'markPending') {
      await updateTaskStatus(row.uuid, 'PENDING');
    } else if (action === 'addComment') {
      setSelectedTask(row);
      setIsCommentModalOpen(true);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (selectedTaskForStatus) {
      await updateTaskStatus(selectedTaskForStatus.uuid, newStatus);
      setIsStatusModalOpen(false);
      setSelectedTaskForStatus(null);
    }
  };

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (comment.trim() && selectedTask) {
      addComment(selectedTask.uuid, comment);
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) { 
      case 'URGENT': return 'bg-red-600 text-white';
      case 'HIGH': return 'bg-red-400 text-white';
      case 'MEDIUM': return 'bg-yellow-400 text-black';
      case 'LOW': return 'bg-green-400 text-white';
      default: return 'bg-gray-400 text-white';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'COMPLETED': return 'bg-green-500 text-white';
      case 'IN_PROGRESS': return 'bg-blue-500 text-white';
      case 'PENDING': return 'bg-yellow-500 text-black';
      case 'OVERDUE': return 'bg-red-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  useEffect(() => {
    getTodoStats();
  }, []);

  useEffect(() => {
    getMyTodos();
  }, [currentPage, rowsPerPage, searchTerm, statusFilter, priorityFilter]);

  useEffect(() => {
    if (todoList && Array.isArray(todoList)) {
      const updatedRows = todoList.map((task, index) => ({
        uuid: task.id,
        serialNo: (currentPage - 1) * rowsPerPage + index + 1,
        title: task.title.length > 25 ? `${task.title.substring(0, 25)}...` : task.title,
        description: task.description ? (task.description.length > 35 ? `${task.description.substring(0, 35)}...` : task.description) : 'No description',
        priority: <span className={`px-2 py-1 rounded text-xs ${getPriorityBadgeClass(task.priority)}`}>{task.priority}</span>,
        status: <span className={`px-2 py-1 rounded text-xs ${getStatusBadgeClass(task.status)}`}>{task.status}</span>,
        dueDate: task.dueDate ? moment(task.dueDate).format('YYYY-MM-DD') : 'No due date',
        assignedBy: task.assignerDetails?.name || 'System',
        createdAt: moment(task.createdAt).format('YYYY-MM-DD'),
        actions: (
          <div className="flex items-center gap-2">
            <button 
              onClick={() => handleTableAction('view', { uuid: task.id, originalTask: task })}
              className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg text-xs hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-1"
              title="View Details"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View
            </button>
            
            <button
              onClick={() => handleTableAction('changeStatus', { uuid: task.id, originalTask: task })}
              className="px-3 py-1.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg text-xs hover:from-orange-600 hover:to-orange-700 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-1"
              title="Change Status"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Status
            </button>

            <button
              onClick={() => handleTableAction('addComment', { uuid: task.id })}
              className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg text-xs hover:from-purple-600 hover:to-purple-700 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-1"
              title="Add Comment"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Comment
            </button>
          </div>
        ),
        originalTask: task
      }));
      setTableRow(updatedRows);
    } else {
      setTableRow([]);
    }
  }, [todoList, currentPage, rowsPerPage]);

  return (
    <>
      {/* Stats Cards */}
      {todoStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-blue-500 text-white p-4 rounded-lg">
            <h3 className="text-lg font-semibold">My Total Tasks</h3>
            <p className="text-2xl">{todoStats.myTotal || 0}</p>
          </div>
          <div className="bg-yellow-500 text-white p-4 rounded-lg">
            <h3 className="text-lg font-semibold">Pending</h3>
            <p className="text-2xl">{todoStats.myPending || 0}</p>
          </div>
          <div className="bg-blue-600 text-white p-4 rounded-lg">
            <h3 className="text-lg font-semibold">In Progress</h3>
            <p className="text-2xl">{todoStats.myInProgress || 0}</p>
          </div>
          <div className="bg-green-500 text-white p-4 rounded-lg">
            <h3 className="text-lg font-semibold">Completed</h3>
            <p className="text-2xl">{todoStats.myCompleted || 0}</p>
          </div>
        </div>
      )}

      <div className='bg-gray-200 dark:bg-gray-800 py-2 px-4 rounded-lg flex justify-between items-center'>
        <div>
          <h1 className='text-2xl font-semibold text-theme-dark dark:text-theme-light'>My Tasks</h1>
          <Breadcrum items={breadcrumbItems} />
        </div>
        <div className='flex justify-end items-center gap-3'>
          <Select
            options={priorityOptions}
            value={priorityFilter}
            onChange={(selected) => setPriorityFilter(selected)}
            placeholder="Filter by Priority"
          />
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={(selected) => setStatusFilter(selected)}
            placeholder="Filter by Status"
          />
        </div>
      </div>

      <div className='mt-4'>
        <ServerTable
          tableColumns={tableColumns}
          title="My Todo List"
          data={tableRow}
          total={totalTodos}
          loading={loading}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          handleTableAction={handleTableAction}
        />
      </div>

      {/* View Task Modal */}
      <Models isOpen={isViewModalOpen} isClose={() => setIsViewModalOpen(false)}>
        {selectedTask && (
          <div className="p-6 max-w-2xl w-full">
            <h2 className="text-xl font-bold mb-4">Task Details</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Title</label>
                <p className="mt-1 text-gray-900">{selectedTask.originalTask?.title}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <p className="mt-1 text-gray-900">{selectedTask.originalTask?.description || 'No description'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Priority</label>
                  <span className={`inline-block mt-1 px-2 py-1 rounded text-xs ${getPriorityBadgeClass(selectedTask.originalTask?.priority)}`}>
                    {selectedTask.originalTask?.priority}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-block mt-1 px-2 py-1 rounded text-xs ${getStatusBadgeClass(selectedTask.originalTask?.status)}`}>
                    {selectedTask.originalTask?.status}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Due Date</label>
                  <p className="mt-1 text-gray-900">{selectedTask.originalTask?.dueDate ? moment(selectedTask.originalTask.dueDate).format('YYYY-MM-DD') : 'No due date'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Assigned By</label>
                  <p className="mt-1 text-gray-900">{selectedTask.originalTask?.assignerDetails?.name || 'System'}</p>
                </div>
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Models>

      {/* Add Comment Modal */}
      <Models isOpen={isCommentModalOpen} isClose={() => setIsCommentModalOpen(false)}>
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">Add Comment</h2>
          <form onSubmit={handleCommentSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 text-gray-700">Comment</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg h-24 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your comment..."
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsCommentModalOpen(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Add Comment
              </button>
            </div>
          </form>
        </div>
      </Models>

      {/* Status Change Modal */}
      <Models isOpen={isStatusModalOpen} isClose={() => setIsStatusModalOpen(false)}>
        <div className="p-6 w-full bg-white dark:bg-gray-800">
          <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Change Task Status</h2>
          
          {selectedTaskForStatus && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Task: {selectedTaskForStatus.originalTask?.title}</p>
              <p className="text-xs text-gray-500 dark:text-gray-500">
                Current Status: 
                <span className={`ml-2 px-2 py-1 rounded text-xs ${getStatusBadgeClass(selectedTaskForStatus.originalTask?.status)}`}>
                  {selectedTaskForStatus.originalTask?.status}
                </span>
              </p>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={() => handleStatusUpdate('PENDING')}
              disabled={selectedTaskForStatus?.originalTask?.status === 'PENDING'}
              className={`w-full text-left px-4 py-3 rounded-lg border transition-colors duration-200 flex items-center gap-3 ${
                selectedTaskForStatus?.originalTask?.status === 'PENDING'
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed border-gray-200 dark:border-gray-600'
                  : 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700 hover:bg-yellow-100 dark:hover:bg-yellow-900/40'
              }`}
            >
              <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
              <div>
                <p className="font-medium">Pending</p>
                <p className="text-xs opacity-70">Task is waiting to be started</p>
              </div>
            </button>

            <button
              onClick={() => handleStatusUpdate('IN_PROGRESS')}
              disabled={selectedTaskForStatus?.originalTask?.status === 'IN_PROGRESS'}
              className={`w-full text-left px-4 py-3 rounded-lg border transition-colors duration-200 flex items-center gap-3 ${
                selectedTaskForStatus?.originalTask?.status === 'IN_PROGRESS'
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed border-gray-200 dark:border-gray-600'
                  : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/40'
              }`}
            >
              <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
              <div>
                <p className="font-medium">In Progress</p>
                <p className="text-xs opacity-70">Task is currently being worked on</p>
              </div>
            </button>

            <button
              onClick={() => handleStatusUpdate('COMPLETED')}
              disabled={selectedTaskForStatus?.originalTask?.status === 'COMPLETED'}
              className={`w-full text-left px-4 py-3 rounded-lg border transition-colors duration-200 flex items-center gap-3 ${
                selectedTaskForStatus?.originalTask?.status === 'COMPLETED'
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed border-gray-200 dark:border-gray-600'
                  : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/40'
              }`}
            >
              <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              <div>
                <p className="font-medium">Completed</p>
                <p className="text-xs opacity-70">Task has been finished</p>
              </div>
            </button>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={() => setIsStatusModalOpen(false)}
              className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
            >
              Cancel
            </button>
          </div>
        </div>
      </Models>
    </>
  );
}
